import React from 'react';
import { Link } from 'react-router-dom';

/**
 * SummaryCard - shared, reusable dashboard card with two layout variants.
 *
 * Fully presentational - all content and behavior is driven by props. Select
 * the layout with the `variant` prop:
 *
 *   - 'stat' (default): a non-clickable summary tile. An icon sits in a
 *     flex-shrink column next to a small label and a large value. Used for
 *     at-a-glance metrics.
 *   - 'action': a clickable quick-action card. An icon inside a colored circle
 *     sits beside a title and supporting description, wrapped in a react-router
 *     Link with a hover-shadow lift.
 *
 * @param {object}   props
 * @param {('stat'|'action')} [props.variant='stat']   Which layout to render.
 *
 * --- 'stat' variant props ---
 * @param {React.ComponentType} props.icon              Lucide icon component, rendered beside the value.
 * @param {string}   [props.iconColor='text-primary-600'] Tailwind text-color class for the icon.
 * @param {string}   props.label                        Small label text above the value.
 * @param {React.ReactNode} props.value                 Large value text.
 *
 * --- 'action' variant props ---
 * @param {React.ComponentType} props.cardIcon          Lucide icon component, rendered in the circle.
 * @param {string}   props.cardTitle                    Heading text.
 * @param {string}   props.cardDescription              Supporting description text.
 * @param {string}   [props.link]                       react-router `to` target for the wrapping Link.
 * @param {string}   [props.color]                      Icon-circle background class (e.g. 'bg-primary-500').
 */
const SummaryCard = ({
  variant = 'stat',
  // stat variant
  icon: Icon,
  iconColor = 'text-primary-600',
  label,
  value,
  // action variant
  cardIcon: CardIcon,
  cardTitle,
  cardDescription,
  link,
  color,
  // The QuickActionCard this replaced forwarded data-testid, and
  // tests/e2e/dashboard/dashboard.spec.js asserts on dashboard-action-* ids
  // (including a count of them). Dropping it silently broke that suite.
  'data-testid': dataTestId,
}) => {
  if (variant === 'action') {
    return (
      <Link
        to={link}
        className="block"
        {...(dataTestId ? { 'data-testid': dataTestId } : {})}
      >
        <div className="bg-white rounded-lg shadow-soft p-6 hover:shadow-lg transition-shadow">
          <div className="flex items-center">
            <div className={`p-3 rounded-full ${color}`}>
              {CardIcon && <CardIcon className="h-6 w-6 text-white" />}
            </div>
            <div className="ml-4">
              <h3 className="text-lg font-medium text-gray-900">{cardTitle}</h3>
              <p className="text-sm text-gray-500">{cardDescription}</p>
            </div>
          </div>
        </div>
      </Link>
    );
  }
  return (
    <div
      className="bg-white rounded-lg shadow-soft p-6"
      {...(dataTestId ? { 'data-testid': dataTestId } : {})}
    >
      <div className="flex items-center">
        <div className="flex-shrink-0">
          {Icon && <Icon className={`h-8 w-8 ${iconColor}`} />}
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-500">{label}</p>
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );
};

export default SummaryCard;
